/**
 * The research runs started from a hypothesis (*Spustiť výskum*, PRD-0075 R-E3): each run with
 * its status and when it started, opening its research page, and the button that starts one.
 * Research stays verdict-less (ADR-0095): a run shows how far it got, never a judgment — the
 * assessment stays the analyst's.
 */
import { FlaskConical, Loader2, RefreshCw } from 'lucide-react'
import type { ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import type { HypothesisResearchRun, ResearchRunStatus } from '@/types/analytic-hypothesis'
import { formatDateTimeLocalized } from '@/utils/date'
import { HypothesisSection } from './HypothesisSection'

const COPY = 'analyticHypotheses.research'

/** How far a run got, never what it concluded. */
const STATUS_BADGE: Record<ResearchRunStatus, ComponentProps<typeof Badge>['variant']> = {
  queued: 'muted',
  running: 'info',
  finished_success: 'outline',
  finished_partial: 'warning',
  finished_error: 'destructive',
}

export type ResearchRunsView =
  | { status: 'loading' }
  | { status: 'failed'; onRetry: () => void }
  | { status: 'ready'; runs: readonly HypothesisResearchRun[] }

/** *Spustiť výskum*, for a collaborator on an open hypothesis. */
export interface ResearchStart {
  onStart: () => void
  pending: boolean
}

export interface HypothesisResearchSectionProps {
  view: ResearchRunsView
  /** The research page a run belongs to. */
  runHref: (run: HypothesisResearchRun) => string
  /** Null for a reader or a closed hypothesis. */
  start: ResearchStart | null
}

function RunRow({ run, href }: { run: HypothesisResearchRun; href: string }) {
  const { t, i18n } = useTranslation()
  return (
    <li>
      <Link
        to={href}
        aria-label={t(`${COPY}.open`, { title: run.title })}
        className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border/60 px-3 py-2 text-sm transition-colors hover:bg-muted/50"
      >
        <FlaskConical aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-foreground">{run.title}</span>
        <Badge variant={STATUS_BADGE[run.status]} data-run-status={run.status}>
          {t(`${COPY}.status.${run.status}`)}
        </Badge>
        <span className="font-mono text-2xs text-muted-foreground">
          {formatDateTimeLocalized(run.started_at, i18n.language)}
        </span>
      </Link>
    </li>
  )
}

function Runs({ view, runHref }: Pick<HypothesisResearchSectionProps, 'view' | 'runHref'>) {
  const { t } = useTranslation()
  if (view.status === 'loading') {
    return <Skeleton aria-hidden className="h-10 w-full" />
  }
  if (view.status === 'failed') {
    return (
      <div role="alert" className="flex flex-wrap items-center gap-2 text-sm">
        <p className="text-destructive">{t(`${COPY}.loadFailed`)}</p>
        <Button type="button" variant="outline" size="xs" onClick={view.onRetry}>
          <RefreshCw aria-hidden />
          {t('analyticHypotheses.detail.retry')}
        </Button>
      </div>
    )
  }
  if (view.runs.length === 0) {
    return <p className="text-sm text-muted-foreground">{t(`${COPY}.empty`)}</p>
  }
  return (
    <ul className="flex flex-col gap-1.5">
      {view.runs.map(run => (
        <RunRow key={run.id} run={run} href={runHref(run)} />
      ))}
    </ul>
  )
}

export function HypothesisResearchSection({
  view,
  runHref,
  start,
}: HypothesisResearchSectionProps) {
  const { t } = useTranslation()
  const action = start && (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={start.pending}
      onClick={start.onStart}
    >
      {start.pending ? (
        <Loader2 aria-hidden className="animate-spin" />
      ) : (
        <FlaskConical aria-hidden />
      )}
      {t(start.pending ? `${COPY}.starting` : `${COPY}.start`)}
    </Button>
  )
  return (
    <HypothesisSection title={t(`${COPY}.title`)} hint={t(`${COPY}.hint`)} action={action}>
      <Runs view={view} runHref={runHref} />
    </HypothesisSection>
  )
}
